import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTextHeight,
  faExternalLinkAlt,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faArrowsAltH,
  faArrowsAltV,
  faBold,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import MessageBox from './MessageBox';

/**
 * MCQs → Formatting — action buttons for EduPage quiz formatting.
 */
export default function MCQsFormatting() {
  const [running, setRunning] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });

  const showMessage = (text, type = 'info') => {
    if (type !== 'error') return;
    setMessage({ show: true, text, type });
  };
  const closeMessage = () => setMessage({ show: false, text: '', type: 'info' });

  const sendToExtension = useCallback((action, extra = {}) => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', onResp);
        resolve({ noExtension: true });
      }, 2000);

      const onResp = (e) => {
        if (e.source !== window) return;
        const d = e.data;
        if (!d || d.fastgraderResponse !== true) return;
        clearTimeout(timeout);
        window.removeEventListener('message', onResp);
        resolve({
          ok: d.ok,
          error: d.error,
          started: d.started,
          activated: d.edupageTabActivated,
          contextInvalidated: !!d.contextInvalidated,
          count: d.count,
          message: d.message,
        });
      };

      window.addEventListener('message', onResp);
      window.postMessage({
        fastgraderTarget: 'edupage-extension',
        action,
        ...extra,
      }, '*');
    });
  }, []);

  const handleExtensionResult = useCallback((result, successText) => {
    if (result.noExtension) {
      setLastResult({ ok: false, text: 'Extension did not respond' });
      showMessage(
        'MCQs on Edupage extension did not respond.\n\n' +
          '1. Open chrome://extensions\n' +
          '2. Click Reload on "MCQs on Edupage"\n' +
          '3. Hard-reload this page (Ctrl/Cmd+Shift+R)\n' +
          '4. Open the quiz editor on EduPage, then try again.',
        'error'
      );
      return;
    }

    if (result.contextInvalidated) {
      setLastResult({ ok: false, text: 'Extension was reloaded — hard-reload this page' });
      showMessage(
        result.error ||
          'The extension was reloaded while this page was open. Hard-reload this page (Ctrl/Cmd+Shift+R), then try again.',
        'error'
      );
      return;
    }

    if (!result.ok) {
      const err = result.error || 'Unknown error';
      setLastResult({ ok: false, text: err });
      if (!/Opened EduPage/i.test(err)) showMessage(err, 'error');
      return;
    }

    const text = result.message
      || (result.started ? 'Running on EduPage — watch that tab' : successText || 'Sent to EduPage');
    setLastResult({ ok: true, text });
    showMessage(text, 'success');
  }, []);

  const runAction = useCallback(async (key, action, extra, successText) => {
    if (running) return;
    setRunning(key);
    setLastResult(null);
    const result = await sendToExtension(action, extra);
    setRunning(null);
    handleExtensionResult(result, successText);
  }, [running, sendToExtension, handleExtensionResult]);

  const busy = !!running;

  const btn = (bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 20px',
    border: 'none',
    borderRadius: 8,
    background: busy ? '#94a3b8' : bg,
    color: '#fff',
    fontSize: 15,
    fontWeight: 650,
    cursor: busy ? 'wait' : 'pointer',
  });

  const outlineBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 20px',
    border: '1px solid var(--border-color, #cbd5e1)',
    borderRadius: 8,
    background: 'var(--bg-secondary, #fff)',
    color: 'var(--text-primary, #111)',
    fontSize: 15,
    fontWeight: 650,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.7 : 1,
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900 }}>
      <div
        className="panel"
        style={{
          padding: '28px 32px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <button
          type="button"
          disabled={busy}
          style={btn('#0891B2')}
          onClick={() => runAction(
            'fonts',
            'format-fonts-big',
            {},
            'Started on EduPage. Font Size → Big is running.'
          )}
        >
          <FontAwesomeIcon icon={running === 'fonts' ? faSpinner : faTextHeight} spin={running === 'fonts'} />
          {running === 'fonts' ? 'Starting…' : 'Make fonts Big'}
        </button>

        <button
          type="button"
          disabled={busy}
          style={btn('#7c3aed')}
          onClick={() => runAction(
            'bold',
            'format-bold-statements',
            {},
            'Started on EduPage. Bolding MCQ statements only.'
          )}
        >
          <FontAwesomeIcon icon={running === 'bold' ? faSpinner : faBold} spin={running === 'bold'} />
          {running === 'bold' ? 'Starting…' : 'Bold statements only'}
        </button>

        <button
          type="button"
          disabled={busy}
          style={btn('#0d9488')}
          onClick={() => runAction(
            'horizontal',
            'format-mcq-layout',
            { direction: 'horizontal' },
            'Started on EduPage. Setting options to horizontal.'
          )}
        >
          <FontAwesomeIcon icon={running === 'horizontal' ? faSpinner : faArrowsAltH} spin={running === 'horizontal'} />
          {running === 'horizontal' ? 'Starting…' : 'Make options Horizontal'}
        </button>

        <button
          type="button"
          disabled={busy}
          style={btn('#0369a1')}
          onClick={() => runAction(
            'vertical',
            'format-mcq-layout',
            { direction: 'vertical' },
            'Started on EduPage. Setting options to vertical.'
          )}
        >
          <FontAwesomeIcon icon={running === 'vertical' ? faSpinner : faArrowsAltV} spin={running === 'vertical'} />
          {running === 'vertical' ? 'Starting…' : 'Make options Vertical'}
        </button>

        <button
          type="button"
          disabled={busy}
          style={btn('#b45309')}
          onClick={() => runAction(
            'warnings',
            'resolve-result-warnings',
            {},
            'Checked EduPage for result warnings.'
          )}
        >
          <FontAwesomeIcon icon={running === 'warnings' ? faSpinner : faWrench} spin={running === 'warnings'} />
          {running === 'warnings' ? 'Starting…' : 'Fix result warnings'}
        </button>

        <button
          type="button"
          disabled={busy}
          style={outlineBtn}
          onClick={() => window.open('https://fast.edupage.org/elearning/', '_blank', 'noopener,noreferrer')}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
          Open EduPage e-learning
        </button>

        {lastResult && (
          <div
            style={{
              flexBasis: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 8,
              background: lastResult.ok ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: lastResult.ok ? '#047857' : '#b91c1c',
              fontSize: 14,
            }}
          >
            <FontAwesomeIcon icon={lastResult.ok ? faCheckCircle : faExclamationTriangle} />
            {lastResult.text}
          </div>
        )}
      </div>

      <MessageBox message={message} onClose={closeMessage} />
    </div>
  );
}
